/**
 * Renders a CourtsApp MCP tool's MCP Apps HTML card in a sandboxed iframe —
 * the same cards ChatGPT and Claude show — via the spec's host bridge
 * (@modelcontextprotocol/ext-apps AppBridge, 1.x to match the cards' App).
 *
 * The card gets the tool input and result; it can ask us to open a link, send a
 * chat message as the visitor (e.g. "Book 6 PM at…"), or resize. Anything else
 * (server tool calls, display modes) is not offered to it.
 */
import React, { useContext, useEffect, useRef, useState } from "react";
import { AppBridge, PostMessageTransport } from "@modelcontextprotocol/ext-apps/app-bridge";
import type { McpUiPayload } from "../../useToolResults";
import { CardsSkeleton } from "../Skeleton";
import { McpAppHostContext, loadUiResource, withCsp } from "./host";

const MIN_HEIGHT = 80;
const MAX_HEIGHT = 900;
const INITIAL_HEIGHT = 320;

type Props = {
  tool: string;
  ui: McpUiPayload;
  /** Shown if the card can't load (no host, fetch failed). */
  fallback?: React.ReactNode;
};

export function McpAppCard({ tool, ui, fallback = null }: Props) {
  const host = useContext(McpAppHostContext);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [html, setHtml] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [height, setHeight] = useState(INITIAL_HEIGHT);
  // Latest host without re-running the bridge effect when the panel re-renders.
  const hostRef = useRef(host);
  hostRef.current = host;

  useEffect(() => {
    if (!host) {
      setFailed(true);
      return;
    }
    let live = true;
    loadUiResource(host.serverUrl, ui.resourceUri).then(
      (r) => live && setHtml(withCsp(r.html, r.csp)),
      (err) => {
        console.warn("[McpAppCard] card unavailable", ui.resourceUri, err);
        if (live) setFailed(true);
      },
    );
    return () => {
      live = false;
    };
  }, [host?.serverUrl, ui.resourceUri]);

  useEffect(() => {
    const iframe = iframeRef.current;
    const win = iframe?.contentWindow;
    if (!html || !iframe || !win) return;

    const bridge = new AppBridge(
      null,
      { name: "Courtney", version: "1.0.0" },
      { openLinks: {}, logging: {} },
      { hostContext: { theme: "light", displayMode: "inline", platform: "web" } },
    );
    bridge.onopenlink = async ({ url }) => {
      if (/^https?:\/\//i.test(url)) window.open(url, "_blank", "noopener,noreferrer");
      return {};
    };
    bridge.onmessage = async ({ content }) => {
      const text = (content ?? [])
        .map((c) => (c.type === "text" ? c.text : ""))
        .filter(Boolean)
        .join("\n")
        .trim();
      if (text) await hostRef.current?.sendText(text);
      return {};
    };
    bridge.onsizechange = ({ height: h }) => {
      if (typeof h === "number" && h > 0) {
        setHeight(Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, Math.ceil(h))));
      }
    };
    bridge.oninitialized = () => {
      void bridge.sendToolInput({ arguments: ui.input });
      void bridge.sendToolResult(ui.result as Parameters<AppBridge["sendToolResult"]>[0]);
    };

    let cancelled = false;
    // Listen before the card's script runs: connect on the (stable) contentWindow,
    // then load the HTML, so its ui/initialize can't arrive before we're ready.
    void bridge
      .connect(new PostMessageTransport(win, win))
      .then(() => {
        if (!cancelled) iframe.srcdoc = html;
      })
      .catch((err) => {
        console.warn("[McpAppCard] bridge failed", err);
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      void bridge.teardownResource({}).catch(() => {});
      void bridge.close().catch(() => {});
    };
  }, [html, ui]);

  if (failed) return <>{fallback}</>;
  if (!html) return <CardsSkeleton tool={tool} />;
  return (
    <div className="lk-widget-mcp-app" data-resource-uri={ui.resourceUri}>
      <iframe
        ref={iframeRef}
        title="CourtsApp results"
        // No allow-same-origin: the card runs in an opaque origin, isolated from
        // the host page and the widget. Popups so its links can open a tab.
        sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms"
        style={{ width: "100%", height, border: 0, display: "block", background: "transparent" }}
      />
    </div>
  );
}
